import type * as React from 'react';

import { HTTPError, type HTTPStatusCode, NoCaptureErrorBoundary } from '@gitbook/util-debug';

type FallbackRender = (props: { error: HTTPError; resetError: () => void }) => React.ReactElement;
type Fallback = React.ReactElement | FallbackRender | null;

/**
 * Handle API errors and display a fallback component based on the error status code.
 * Only 4xx status codes are handled by this component.
 * 5xx status codes are not handled and will be thrown, use a classic ErrorBoundary to handle them.
 */
export function APIErrorBoundary(props: {
    fallback: Partial<Record<HTTPStatusCode, Fallback>>;
    resetKeys?: unknown[];
    children: React.ReactNode;
}) {
    return (
        <NoCaptureErrorBoundary
            resetKeys={props.resetKeys}
            fallbackRender={({ error, resetErrorBoundary }) => {
                if (error instanceof HTTPError) {
                    if (!error.expose) {
                        throw error;
                    }

                    const fallback = props.fallback[error.statusCode];

                    if (fallback !== undefined) {
                        if (typeof fallback === 'function') {
                            return fallback({ error, resetError: () => resetErrorBoundary() });
                        }
                        return fallback;
                    }

                    throw error;
                }
                throw error;
            }}
        >
            {props.children}
        </NoCaptureErrorBoundary>
    );
}
